import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/auth";
import { fmtEventDateTime, fmtCutoff, formatPrice } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Calendar, Check, X, ChevronRight, ChevronDown, ChevronUp, CheckCircle2, XCircle } from "lucide-react";
import PageHeader from "@/components/PageHeader";

const GREEN = "#0F5132";
const PAST_BLUE = "#1E3A8A";
const CANCELLED_RED = "#8B0000";

function bucket(bookings) {
  const now = Date.now();
  const upcoming = [], past = [], dead = [];
  const startsMs = (b) => {
    const s = b.event?.starts_at;
    if (!s) return null;
    const t = new Date(s).getTime();
    return Number.isFinite(t) ? t : null;
  };
  for (const b of bookings) {
    const cancelled = (b.status || "").startsWith("cancelled") || b.status === "failed";
    if (cancelled) { dead.push(b); continue; }
    const t = startsMs(b);
    if (t !== null && t < now && b.status === "confirmed") { past.push(b); continue; }
    upcoming.push(b);
  }
  upcoming.sort((a, b) => (startsMs(a) ?? Infinity) - (startsMs(b) ?? Infinity));
  past.sort((a, b) => (startsMs(b) ?? 0) - (startsMs(a) ?? 0));
  dead.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  return { upcoming, past, dead };
}

function statusIcon(b) {
  const rs = (b.refund_status || "").toLowerCase();
  if (rs && rs !== "failed") return { letter: "R", color: "#DC143C", label: "Refunded" };
  const st = (b.status || "").toLowerCase();
  if (st.startsWith("cancelled") || st === "failed") return { letter: "C", color: CANCELLED_RED, label: "Cancelled" };
  if (st === "pending_payment") return { letter: "P", color: "#B8860B", label: "Payment pending" };
  if (st === "confirmed") return { letter: null, color: GREEN, label: "Confirmed", tick: true };
  return { letter: "?", color: "#64748B", label: st || "Unknown" };
}

function BookingCard({ b }) {
  const st = statusIcon(b);
  const amount = b.amount_pence === 0 ? "Free" : formatPrice(b.amount_pence, b.currency);
  const cutoffIso = b.event?.bookings_close_at || b.event?.starts_at || null;
  const cutoffPassed = cutoffIso ? new Date(cutoffIso).getTime() <= Date.now() : false;
  return (
    <Link to={`/bookings/${b.booking_id}`} data-testid={`booking-${b.booking_id}`} className="block group">
      <Card className="overflow-hidden border-border transition-shadow group-hover:shadow-md">
        <div className="flex items-center justify-between px-4 py-3 text-white gap-3" style={{ backgroundColor: GREEN }}>
          <div className="text-sm font-semibold flex-1 min-w-0 truncate" data-testid={`booking-date-${b.booking_id}`}>
            {fmtEventDateTime(b.event?.starts_at) || "Date TBC"}
          </div>
          <div
            className="shrink-0 w-7 h-7 rounded-full bg-white grid place-items-center"
            aria-label={st.label}
            data-testid={`booking-status-${b.booking_id}`}
          >
            {st.tick ? <Check size={14} style={{ color: st.color }} strokeWidth={3} /> :
              <span className="text-sm font-semibold" style={{ color: st.color }}>{st.letter}</span>}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-lg font-semibold leading-snug">{b.event_name}</h3>
            <ChevronRight className="text-muted-foreground shrink-0 mt-1" size={18} />
          </div>
          {cutoffIso && (
            <div className="text-xs text-muted-foreground mt-2" data-testid={`booking-cutoff-${b.booking_id}`}>
              Last date for bookings/cancellations: <span className="font-medium text-foreground">{fmtCutoff(cutoffIso)}</span>
              {cutoffPassed ? " — cancellations closed" : ""}
            </div>
          )}
          <div className="text-sm text-muted-foreground mt-1">
            {b.places} place{b.places === 1 ? "" : "s"} · {amount}
          </div>
        </div>
      </Card>
    </Link>
  );
}

function Section({ open, onToggle, title, icon: Icon, accent, items, empty, testId }) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden mb-4" style={{ borderLeft: `5px solid ${accent}` }}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40" data-testid={testId}>
        <div className="flex items-center gap-3">
          <Icon size={18} style={{ color: accent }} />
          <span className="text-sm font-semibold">{title}</span>
          <span className="text-xs font-semibold text-white rounded-full px-2 py-0.5" style={{ backgroundColor: accent }}>{items.length}</span>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="border-t border-border px-4 py-3">
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-2">{empty}</div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {items.map((b) => <BookingCard key={b.booking_id} b={b} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BookingsPage() {
  const api = useApi();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ upcoming: true, past: false, dead: false });

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/bookings");
        const data = await r.json();
        if (r.ok) setBookings(data.bookings || []);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const buckets = useMemo(() => bucket(bookings), [bookings]);

  return (
    <div>
      <PageHeader title="My bookings" subtitle="All your event reservations" testId="bookings-page-header" />
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : bookings.length === 0 ? (
        <Card className="p-10 text-center">
          <Calendar className="mx-auto text-muted-foreground" size={32} />
          <h3 className="mt-3 font-semibold">No bookings yet</h3>
          <p className="text-sm text-muted-foreground mt-1">Browse the Events tab to book your first event.</p>
        </Card>
      ) : (
        <>
          <Section open={expanded.upcoming} onToggle={() => setExpanded((e) => ({ ...e, upcoming: !e.upcoming }))}
            title="Upcoming events" icon={Calendar} accent={GREEN} items={buckets.upcoming}
            empty="No upcoming bookings." testId="bookings-section-upcoming" />
          <Section open={expanded.past} onToggle={() => setExpanded((e) => ({ ...e, past: !e.past }))}
            title="Past events" icon={CheckCircle2} accent={PAST_BLUE} items={buckets.past}
            empty="No past confirmed bookings yet." testId="bookings-section-past" />
          <Section open={expanded.dead} onToggle={() => setExpanded((e) => ({ ...e, dead: !e.dead }))}
            title="Cancelled / failed" icon={XCircle} accent={CANCELLED_RED} items={buckets.dead}
            empty="Nothing cancelled or failed." testId="bookings-section-dead" />
        </>
      )}
    </div>
  );
}
